import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { DataGrid, Modal } from "../../common";
import { selectedOrgIdAtom, toastOptionsAtom, userAtom } from "../../../atoms";
import { useFetchOrganizationsQuery } from "../../../api";
import {
  useFetchPayRatesQuery,
  useUpdatePayRateMutation,
} from "../../../api/userService";
import { OrganizationModel } from "../../../models";
import {
  CREW_OVERTIME_MULTIPLIER,
  CREW_ROLE_CHIP_COLORS,
  CREW_ROLE_LABELS,
  CREW_SKILL_LEVELS,
  PAY_RATE_ALL_FILTER,
  PAY_RATE_ROLE_FILTERS,
} from "../../../constants";
import { Role } from "../../../enums";
import { DropdownItem, PayRate, PayRateFormData } from "../../../types";
import { formatCurrency, getApiErrorMessage, theme } from "../../../utils";
import { CrewFilterChips } from "../../users/crew/fragments";
import { PayRateForm, PayRateSummaryCards } from "./fragments";

const getSkillLevelLabel = (payRate: PayRate) =>
  CREW_SKILL_LEVELS.find((level) => level.value === payRate.skillLevel)?.key ??
  null;

const getRateName = (payRate: PayRate) => {
  const roleLabel = CREW_ROLE_LABELS[payRate.roleCode];
  const skillLevelLabel = getSkillLevelLabel(payRate);

  return skillLevelLabel ? `${roleLabel} — ${skillLevelLabel}` : roleLabel;
};

const RateCell = ({
  amount,
  caption,
}: {
  amount: number | null;
  caption: string;
}) =>
  amount === null ? (
    <Typography sx={{ fontSize: 13, color: "var(--dashboard-text-secondary)" }}>
      Not set
    </Typography>
  ) : (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
        {formatCurrency(amount)}
      </Typography>
      <Typography
        sx={{ fontSize: 11, color: "var(--dashboard-text-secondary)" }}
      >
        {caption}
      </Typography>
    </Box>
  );

export const PayRates = () => {
  const [user] = useAtom(userAtom);
  const [queryParams] = useSearchParams();
  const [selectedOrgId, setSelectedOrgId] = useAtom(selectedOrgIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [roleFilter, setRoleFilter] = useState(PAY_RATE_ALL_FILTER);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [selectedPayRate, setSelectedPayRate] = useState<PayRate>();
  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const isSuperAdmin = user?.roleCode === Role.SuperAdmin;
  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: isSuperAdmin,
  });
  const fetchPayRatesQuery = useFetchPayRatesQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });
  const payRates = useMemo(
    () => fetchPayRatesQuery.data ?? [],
    [fetchPayRatesQuery.data],
  );
  const updatePayRateMutation = useUpdatePayRateMutation();

  useEffect(() => {
    if (fetchPayRatesQuery.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [fetchPayRatesQuery.isError]);

  useEffect(() => {
    if (updatePayRateMutation.isSuccess) {
      setSelectedPayRate(undefined);
      fetchPayRatesQuery.refetch();
      setToastOptions({
        open: true,
        message: "Pay rate updated successfully",
        severity: "success",
      });
    }

    if (updatePayRateMutation.isError) {
      if (
        updatePayRateMutation.error.response?.status === HttpStatusCode.NotFound
      ) {
        setSelectedPayRate(undefined);
        fetchPayRatesQuery.refetch();
      }

      setToastOptions({
        open: true,
        message: getApiErrorMessage(updatePayRateMutation.error),
        severity: "error",
      });
    }
  }, [
    updatePayRateMutation.isSuccess,
    updatePayRateMutation.data,
    updatePayRateMutation.isError,
    updatePayRateMutation.error,
  ]);

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      setOrganizationsDropdown(
        OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      );
    } else if (fetchOrganizationsQuery.isError) {
      setOrganizationsDropdown([]);
    }
  }, [
    fetchOrganizationsQuery.isSuccess,
    fetchOrganizationsQuery.isError,
    fetchOrganizationsQuery.data,
  ]);

  useEffect(() => {
    if (organizationsDropdown.length > 0 && !selectedOrgId) {
      setSelectedOrgId(
        Number(queryParams.get("orgId")) ||
          user?.orgId ||
          (organizationsDropdown[0].value as number),
      );
    }
  }, [organizationsDropdown, selectedOrgId, setSelectedOrgId]);

  const filteredPayRates = useMemo(
    () =>
      payRates.filter(
        (payRate) =>
          roleFilter === PAY_RATE_ALL_FILTER || payRate.roleCode === roleFilter,
      ),
    [payRates, roleFilter],
  );

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCloseForm = () => {
    setSelectedPayRate(undefined);
  };

  const handleSavePayRate = (data: PayRateFormData) => {
    if (!selectedPayRate) return;

    updatePayRateMutation.mutate({
      orgId: selectedOrgId,
      id: selectedPayRate.id,
      baseRate: data.baseRate,
    });
  };

  const columns: GridColDef<PayRate>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 200,
      valueGetter: (_value, row) => getRateName(row),
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--dashboard-text-primary)",
          }}
        >
          {getRateName(row)}
        </Typography>
      ),
    },
    {
      field: "roleCode",
      headerName: "Role",
      width: 140,
      valueGetter: (_value, row) => CREW_ROLE_LABELS[row.roleCode],
      renderCell: ({ row }) => (
        <Chip
          label={CREW_ROLE_LABELS[row.roleCode]}
          size="small"
          variant="outlined"
          color={CREW_ROLE_CHIP_COLORS[row.roleCode] ?? "default"}
          sx={{ borderRadius: "6px", fontWeight: 500 }}
        />
      ),
    },
    {
      field: "skillLevel",
      headerName: "Skill Level",
      width: 140,
      valueGetter: (_value, row) => getSkillLevelLabel(row),
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: 13,
            color: getSkillLevelLabel(row)
              ? "var(--dashboard-text-primary)"
              : "var(--dashboard-text-secondary)",
          }}
        >
          {getSkillLevelLabel(row) ?? "—"}
        </Typography>
      ),
    },
    {
      field: "baseRate",
      headerName: "Base Rate",
      width: 140,
      renderCell: ({ row }) => (
        <RateCell amount={row.baseRate} caption="base / hr" />
      ),
    },
    {
      field: "overtimeRate",
      headerName: `Overtime (${CREW_OVERTIME_MULTIPLIER}×)`,
      width: 150,
      renderCell: ({ row }) => (
        <RateCell amount={row.overtimeRate} caption="overtime / hr" />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Edit">
          <IconButton
            aria-label="edit"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedPayRate(row);
            }}
          >
            <EditOutlinedIcon
              fontSize="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box className="dashboard-page">
      <Box className="dashboard-header">
        <Box sx={{ minWidth: 0 }}>
          <Typography className="dashboard-title">Pay Rates</Typography>
          <Typography className="dashboard-subtitle">
            Hourly rates by role and skill level
          </Typography>
        </Box>

        {isSuperAdmin && (
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              maxWidth: { xs: "100%", sm: 320 },
              backgroundColor: "var(--dashboard-surface)",
              borderRadius: "var(--dashboard-radius-control)",
            }}
          >
            <InputLabel>Organization</InputLabel>
            <Select
              value={organizationsDropdown.length ? selectedOrgId : ""}
              label="Organization"
              onChange={(event) => setSelectedOrgId(Number(event.target.value))}
              sx={{
                height: 42,
                borderRadius: "var(--dashboard-radius-control)",
              }}
            >
              {organizationsDropdown.map((organization) => (
                <MenuItem key={organization.value} value={organization.value}>
                  {organization.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <PayRateSummaryCards payRates={payRates} />

      <Box className="dashboard-card" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid var(--dashboard-border-row)",
          }}
        >
          <CrewFilterChips
            label="Role"
            options={PAY_RATE_ROLE_FILTERS}
            value={roleFilter}
            onChange={handleRoleFilterChange}
          />
        </Box>

        <DataGrid
          columns={columns}
          rows={filteredPayRates}
          loading={
            fetchPayRatesQuery.isLoading ||
            fetchPayRatesQuery.isRefetching ||
            updatePayRateMutation.isLoading
          }
          checkboxSelection={false}
          autoHeight
          rowHeight={64}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onRowClick={({ row }) => setSelectedPayRate(row)}
          sx={{
            border: "none",
            px: 2,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--dashboard-text-secondary)",
            },
          }}
        />

        <Typography className="dashboard-helper-text" sx={{ px: 2, pb: 2 }}>
          Overtime is calculated automatically as {CREW_OVERTIME_MULTIPLIER}×
          the base rate
        </Typography>
      </Box>

      <Modal
        open={!!selectedPayRate}
        onClose={handleCloseForm}
        title={selectedPayRate ? getRateName(selectedPayRate) : ""}
        content={
          <PayRateForm
            id="payRateForm"
            defaultValues={selectedPayRate}
            handleEdit={handleSavePayRate}
          />
        }
        form="payRateForm"
        primaryButtonText="Save Changes"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleCloseForm}
        disablePrimaryButton={updatePayRateMutation.isLoading}
        sx={{
          modal: {
            width: { xs: "90%", sm: "70%", md: "55%", lg: "40%" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            overflowY: "auto",
            flex: 1,
          },
        }}
      />
    </Box>
  );
};
